/**
 * useFeaturedTestimonials — returns a curated set of recent, high-signal
 * testimonials from the NoJo proxy feed.
 *
 * Rules (applied client-side after fetching the most recent batch):
 *   - Only items from the last `recentMonths` (default 18 months) are kept.
 *     If the filter would return < `minItems`, falls back to "newest first
 *     without date filter" so the rotator never empties out.
 *   - Quote length is clamped to a reasonable signal window (no two-word
 *     blurbs, no walls). `minLen` and `maxLen` tune this.
 *   - When a `serviceId` is provided (`japanetccard` | `japantollreceipts`
 *     | `japanchangemoney`), the result is restricted to reviews that
 *     came from that specific sister-site feed. This is what powers the
 *     service-tuned spotlight + grid on each detail page. If the filtered
 *     set is smaller than `minItems`, falls back to the unfiltered set
 *     so the rotator never empties out on a service-specific surface.
 *   - Returns at most `take` items.
 *
 * Backed by `/api/integrations/testimonials` — same endpoint as the full
 * page, just trimmed for site-wide reuse (home hero rotator, service detail
 * customer band, etc.).
 */
import { useEffect, useState } from "react";

import { fetchJsonDeduped } from "@/lib/dedupedFetch";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function useFeaturedTestimonials({
  lang = "en",
  recentMonths = 18,
  minLen = 35,
  maxLen = 320,
  take = 6,
  fetchSize = 50,
  serviceId = null,
} = {}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [sourceFreshness, setSourceFreshness] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        // Reset immediately so a lang/service change can never leave
        // the PREVIOUS language's/service's quote visibly on screen
        // while the new request is in flight — the component's own
        // "loading && items.length === 0" guard hides the section
        // instead of showing stale content for the gap.
        setItems([]);
        // When asking for a specific service, fetch a larger window so
        // there's enough headroom to filter down to `take` items.
        const requestSize = serviceId ? Math.max(fetchSize, 120) : fetchSize;
        const params = new URLSearchParams({
          lang,
          per_page: String(requestSize),
          page: "0",
        });
        if (serviceId) {
          // Route the proxy directly to that sister-site's testimonials
          // endpoint so we don't have to client-side-filter a federated
          // feed that may not actually carry that service's reviews.
          params.set("service", serviceId);
        }
        // `take`/`minLen`/`maxLen` never reach the URL (they're applied
        // client-side below), so a "spotlight" instance and a "grid"
        // instance for the same service resolve to the identical URL —
        // fetchJsonDeduped collapses that pair into one network call.
        const url = `${BACKEND}/api/integrations/testimonials?${params.toString()}`;
        const json = await fetchJsonDeduped(url);
        if (cancelled) return;
        setSourceFreshness(Array.isArray(json.sourceFreshness) ? json.sourceFreshness : []);
        if (!json.ok) {
          setError(true);
          setLoading(false);
          return;
        }
        // Language-authenticity gate — Root cause of an owner-reported
        // display bug: the length filter alone (`minLen`/`maxLen`) checks
        // character COUNT only, and JA thresholds are deliberately lower
        // than EN's (Japanese conveys the same content in fewer
        // characters — see the mode-level comment in TestimonialRotator).
        // But a handful of items have NOT been translated yet
        // (`translated: false`) and still carry their raw ENGLISH
        // `summary` text at that same reduced-lookin-length — e.g. "Very
        // useful and very convenient for long travels" is 48 characters,
        // comfortably clearing the JA grid's minLen=25, even though it is
        // plain English. Without this gate those items were rendering as
        // literal English quotes on `/ja` pages. On the JA locale, only
        // accept items the backend has actually confirmed are Japanese
        // (`translated === true`) — this drops zero EN-locale content
        // (the check is skipped entirely when `lang !== "ja"`) and
        // invents nothing: it only prevents already-untranslated English
        // text from being mis-displayed as a Japanese testimonial.
        const isJaLocale = lang === "ja";
        const all = (Array.isArray(json.items) ? json.items : []).filter(
          (it) => {
            const q = (it.summary || "").trim();
            if (q.length < minLen || q.length > maxLen) return false;
            if (isJaLocale && !it.translated) return false;
            return true;
          }
        );
        const now = Date.now();
        const cutoff = now - recentMonths * 30 * 24 * 60 * 60 * 1000;
        const recent = all.filter((it) => {
          const ts = new Date(it.updatedAt).getTime();
          return Number.isFinite(ts) && ts >= cutoff;
        });
        let pool = recent.length >= take ? recent : all;

        // Service-specific narrowing — owner directive: JTR/JEC detail
        // pages must only show reviews from THEIR sister site, never
        // cross-contaminate. Fall back to the cross-service pool only
        // if the filtered result would be too thin to render the
        // configured surface.
        if (serviceId) {
          const filtered = pool.filter((it) => it.serviceId === serviceId);
          pool = filtered.length >= Math.min(take, 3) ? filtered : pool;
        }

        const final = pool.slice(0, take);
        setItems(final);
        setError(false);
      } catch (e) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lang, recentMonths, minLen, maxLen, take, fetchSize, serviceId]);

  return { items, loading, error, sourceFreshness };
}
